import React, { useEffect, useState } from 'react';
import {
  AlignCenter, AlignJustify, AlignLeft, AlignRight, ArrowDownToLine, ArrowUpToLine, Bold, Columns3, Download, Filter,
  FolderOpen, History, Image as ImageIcon, IndentDecrease, IndentIncrease, Italic, Keyboard, Link2, List, ListOrdered,
  ListTree, Maximize2, Merge, MessageSquareText, Minus, Moon, MoreHorizontal, PanelTop, Play, Plus, Redo2,
  RemoveFormatting, Rows3, Search, SeparatorHorizontal, SquarePlus, Star, Strikethrough, Sun, Table2, Trash2, Type,
  Underline, Undo2, X, ZoomIn, ZoomOut, AlignVerticalSpaceAround, CopyPlus,
} from 'lucide-react';
import { escucharEstado } from './editorComandos';
import { AuroraLogo } from './icons';

/*
 * Barra inferior del editor en el TELÉFONO (portal y docs.orvian.mx en un navegador móvil).
 *
 * En el teléfono el editor abre con el diseño de la app (`layout=app`): sin reglas, sin tiras laterales,
 * al ancho y SIN la cinta del motor, que es de ratón y pantalla ancha. Lo que la cinta hacía lo ofrece
 * esta barra con los MISMOS comandos que la barra nativa de la app (editorComandos.js):
 *   · edición → Deshacer · Rehacer · Formato · Insertar · Aurora IA · Más
 *     y, con el teclado abierto, una fila rápida (negrita, listas, alineación, tamaño).
 *   · solo lectura (lector, comentarista, invitado, PDF, diagramas) → nada que edite:
 *     Buscar · Comentarios · Ajustar · Aurora IA · Más.
 * Espejo de app/lib/widgets/office_barra.dart: si una cambia de opciones, revisar la otra.
 */

const COLORES = ['#1a1b22', '#5f6368', '#d93025', '#e37400', '#f9ab00', '#188038', '#1a73e8', '#5f51bb', '#9334e6', '#c2185b'];
const FORMATOS_NUMERO = [
  ['General', 'General'],
  ['Número', '#,##0.00'],
  ['Moneda', '"$"#,##0.00'],
  ['Porcentaje', '0.00%'],
  ['Fecha', 'dd/mm/yyyy'],
];
const ALINEACIONES = [
  ['left', AlignLeft, 'Izquierda'],
  ['center', AlignCenter, 'Centro'],
  ['right', AlignRight, 'Derecha'],
  ['justify', AlignJustify, 'Justificar'],
];

// Pulsar un botón no debe quitarle el foco al editor: se cerraría el teclado y se perdería el cursor
const sinRobarFoco = { onPointerDown: (e) => e.preventDefault(), onMouseDown: (e) => e.preventDefault() };

function Hoja({ titulo, onClose, children }) {
  useEffect(() => {
    const onKey = (e) => e.key === 'Escape' && onClose();
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);
  return (
    <div className="zo-hoja-capa" onClick={onClose} role="presentation">
      <div className="zo-hoja" role="dialog" aria-label={titulo} onClick={(e) => e.stopPropagation()}>
        <div className="zo-hoja-asa" />
        <div className="zo-hoja-cabeza">
          <strong>{titulo}</strong>
          <button type="button" className="zo-icon-btn" onClick={onClose} aria-label="Cerrar"><X className="h-5 w-5" /></button>
        </div>
        <div className="zo-hoja-cuerpo">{children}</div>
      </div>
    </div>
  );
}

function Grupo({ titulo, children }) {
  return (
    <section className="zo-hoja-grupo">
      {titulo && <div className="zo-hoja-grupo-titulo">{titulo}</div>}
      <div className="zo-hoja-fila">{children}</div>
    </section>
  );
}

function Cuadro({ icon: Icon, label, activo, onClick, disabled, children }) {
  return (
    <button type="button" className={`zo-cuadro ${activo ? 'is-on' : ''}`} onClick={onClick} disabled={disabled} aria-pressed={activo || undefined} {...sinRobarFoco}>
      {Icon ? <Icon className="h-5 w-5" /> : children}
      <span>{label}</span>
    </button>
  );
}

function BotonBarra({ icon: Icon, label, onClick, disabled, activo, children, testid }) {
  return (
    <button
      type="button"
      className={`zo-barra-btn ${activo ? 'is-on' : ''}`}
      onClick={onClick}
      disabled={disabled}
      aria-label={label}
      data-testid={testid}
      {...sinRobarFoco}
    >
      {Icon ? <Icon className="h-[22px] w-[22px]" /> : children}
      <span>{label}</span>
    </button>
  );
}

function Rapido({ icon: Icon, label, onClick, activo, children }) {
  return (
    <button type="button" className={`zo-rapido ${activo ? 'is-on' : ''}`} onClick={onClick} aria-label={label} title={label} {...sinRobarFoco}>
      {Icon ? <Icon className="h-5 w-5" /> : children}
    </button>
  );
}

function ListaDescargas({ descargas, onDescargar, titulo = 'Descargar como' }) {
  return (
    <section className="zo-hoja-grupo">
      {titulo && <div className="zo-hoja-grupo-titulo">{titulo}</div>}
      <div className="zo-hoja-lista">
        {descargas.map((d) => (
          <button key={d.formato} type="button" className="zo-hoja-lista-fila" onClick={() => onDescargar(d.formato)}>
            <Download className="h-5 w-5" />
            <span>{d.nombre}</span>
          </button>
        ))}
      </div>
    </section>
  );
}

function HojaFormato({ kind, estado, cmd, onClose }) {
  const hoja = kind === 'sheet';
  return (
    <Hoja titulo="Formato" onClose={onClose}>
      <Grupo titulo="Texto">
        <Cuadro icon={Bold} label="Negrita" activo={estado.bold} onClick={() => cmd('bold', !estado.bold)} />
        <Cuadro icon={Italic} label="Cursiva" activo={estado.italic} onClick={() => cmd('italic', !estado.italic)} />
        <Cuadro icon={Underline} label="Subrayado" activo={estado.underline} onClick={() => cmd('underline', !estado.underline)} />
        <Cuadro icon={Strikethrough} label="Tachado" activo={estado.strike} onClick={() => cmd('strike', !estado.strike)} />
      </Grupo>
      <Grupo titulo="Tamaño">
        <Cuadro icon={Minus} label="Reducir" onClick={() => cmd('fontSize', estado.fontSize - 1)} />
        <div className="zo-tamano" aria-live="polite">{estado.fontSize}</div>
        <Cuadro icon={Plus} label="Aumentar" onClick={() => cmd('fontSize', estado.fontSize + 1)} />
      </Grupo>
      <Grupo titulo="Color del texto">
        <div className="zo-colores">
          {COLORES.map((c) => (
            <button key={c} type="button" className="zo-color" style={{ background: c }} aria-label={`Color ${c}`} onClick={() => cmd('color', c)} {...sinRobarFoco} />
          ))}
        </div>
      </Grupo>
      <Grupo titulo="Alineación">
        {ALINEACIONES.filter(([a]) => !hoja || a !== 'justify').map(([a, Icon, label]) => (
          <Cuadro key={a} icon={Icon} label={label} activo={estado.align === a} onClick={() => cmd('align', a)} />
        ))}
      </Grupo>
      {!hoja && (
        <>
          <Grupo titulo="Listas y sangría">
            <Cuadro icon={List} label="Viñetas" onClick={() => cmd('list', 'bullet')} />
            <Cuadro icon={ListOrdered} label="Numerada" onClick={() => cmd('list', 'number')} />
            <Cuadro icon={RemoveFormatting} label="Quitar lista" onClick={() => cmd('list', 'none')} />
            <Cuadro icon={IndentDecrease} label="Menos sangría" onClick={() => cmd('indent', 'less')} />
            <Cuadro icon={IndentIncrease} label="Más sangría" onClick={() => cmd('indent', 'more')} />
          </Grupo>
          <Grupo titulo="Interlineado">
            {[1, 1.15, 1.5, 2].map((v) => (
              <button key={v} type="button" className="zo-pastilla" onClick={() => cmd('lineSpacing', v)} {...sinRobarFoco}>{v}</button>
            ))}
          </Grupo>
        </>
      )}
      {hoja && (
        <>
          <Grupo titulo="Formato de número">
            {FORMATOS_NUMERO.map(([label, formato]) => (
              <button key={label} type="button" className="zo-pastilla" onClick={() => cmd('numberFormat', formato)} {...sinRobarFoco}>{label}</button>
            ))}
          </Grupo>
          <Grupo titulo="Celdas">
            <Cuadro icon={ArrowUpToLine} label="Arriba" onClick={() => cmd('valign', 'top')} />
            <Cuadro icon={AlignVerticalSpaceAround} label="Centro" onClick={() => cmd('valign', 'center')} />
            <Cuadro icon={ArrowDownToLine} label="Abajo" onClick={() => cmd('valign', 'bottom')} />
            <Cuadro icon={Merge} label="Combinar" onClick={() => cmd('mergeCells')} />
          </Grupo>
        </>
      )}
    </Hoja>
  );
}

function HojaInsertar({ kind, cmd, onClose }) {
  const [vista, setVista] = useState(null); // null | 'tabla' | 'enlace'
  const [tabla, setTabla] = useState({ cols: 3, rows: 3 });
  const [enlace, setEnlace] = useState({ url: '', texto: '' });
  const hoja = kind === 'sheet';
  const slides = kind === 'slides';
  const hacer = (c, v) => {
    cmd(c, v);
    onClose();
  };

  if (vista === 'tabla') {
    const paso = (campo, d) => setTabla((t) => ({ ...t, [campo]: Math.max(1, Math.min(20, t[campo] + d)) }));
    return (
      <Hoja titulo="Insertar tabla" onClose={onClose}>
        {[['cols', 'Columnas'], ['rows', 'Filas']].map(([campo, label]) => (
          <div key={campo} className="zo-paso">
            <span>{label}</span>
            <button type="button" className="zo-icon-btn" onClick={() => paso(campo, -1)} aria-label={`Menos ${label.toLowerCase()}`}><Minus className="h-4 w-4" /></button>
            <strong>{tabla[campo]}</strong>
            <button type="button" className="zo-icon-btn" onClick={() => paso(campo, 1)} aria-label={`Más ${label.toLowerCase()}`}><Plus className="h-4 w-4" /></button>
          </div>
        ))}
        <button type="button" className="zo-hoja-primario" onClick={() => hacer('table', tabla)}>Insertar tabla {tabla.cols}×{tabla.rows}</button>
      </Hoja>
    );
  }
  if (vista === 'enlace') {
    const url = enlace.url.trim();
    return (
      <Hoja titulo="Insertar enlace" onClose={onClose}>
        <form
          className="zo-enlace"
          onSubmit={(e) => {
            e.preventDefault();
            if (!url) return;
            hacer('link', { url: /^[a-z][a-z0-9+.-]*:/i.test(url) ? url : `https://${url}`, texto: enlace.texto.trim() });
          }}
        >
          <label>Dirección
            <input type="url" inputMode="url" autoFocus placeholder="https://…" value={enlace.url} onChange={(e) => setEnlace((v) => ({ ...v, url: e.target.value }))} />
          </label>
          <label>Texto que se ve (opcional)
            <input type="text" value={enlace.texto} onChange={(e) => setEnlace((v) => ({ ...v, texto: e.target.value }))} />
          </label>
          <button type="submit" className="zo-hoja-primario" disabled={!url}>Insertar enlace</button>
        </form>
      </Hoja>
    );
  }

  return (
    <Hoja titulo="Insertar" onClose={onClose}>
      <Grupo>
        {slides && <Cuadro icon={CopyPlus} label="Diapositiva" onClick={() => hacer('slideNew')} />}
        <Cuadro icon={ImageIcon} label="Imagen" onClick={() => hacer('image')} />
        {!hoja && <Cuadro icon={Table2} label="Tabla" onClick={() => setVista('tabla')} />}
        <Cuadro icon={Link2} label="Enlace" onClick={() => setVista('enlace')} />
        {kind === 'doc' && <Cuadro icon={SeparatorHorizontal} label="Salto de página" onClick={() => hacer('pageBreak')} />}
        {slides && <Cuadro icon={Trash2} label="Quitar diapositiva" onClick={() => hacer('slideDelete')} />}
      </Grupo>
      {hoja && (
        <Grupo titulo="Filas y columnas">
          <Cuadro icon={Rows3} label="Insertar fila" onClick={() => hacer('insertRows')} />
          <Cuadro icon={Columns3} label="Insertar columna" onClick={() => hacer('insertColumns')} />
          <Cuadro icon={Filter} label="Filtro" onClick={() => hacer('filter')} />
          <Cuadro icon={Trash2} label="Quitar fila" onClick={() => hacer('deleteRows')} />
          <Cuadro icon={Trash2} label="Quitar columna" onClick={() => hacer('deleteColumns')} />
        </Grupo>
      )}
    </Hoja>
  );
}

/**
 * @param edicion   true si quien lo abre puede editar (documento, hoja o presentación); si no, barra de lectura
 * @param teclado   el teclado del teléfono está abierto (fila rápida de formato)
 */
export default function BarraMovil({
  kind, iframe, ready, edicion, comentar, guest, dark, starred, barraCompleta, teclado,
  onComando, onAccion, onTema, onDestacar, onDescargar, onDrive, onAurora, onAuroraChat, onBarraCompleta, onPresentar,
  aurora, descargas = [],
}) {
  const [hoja, setHoja] = useState(null); // 'formato' | 'insertar' | 'mas'
  const [estado, setEstado] = useState({
    bold: false, italic: false, underline: false, strike: false, align: 'left', fontSize: 11, canUndo: false, canRedo: false,
  });

  // Los botones se encienden solos con la selección (mismos avisos que usa la app)
  useEffect(() => {
    if (!iframe || !ready || !edicion) return undefined;
    return escucharEstado(iframe, setEstado);
  }, [iframe, ready, edicion]);

  const cerrar = () => setHoja(null);
  const cmd = (c, v) => onComando(c, v);
  const accion = (a) => {
    cerrar();
    onAccion(a);
  };
  const ocultarTeclado = () => {
    try {
      iframe?.contentDocument?.activeElement?.blur();
    } catch {
      /* sin editor */
    }
  };

  const siguienteAlineacion = { left: 'center', center: 'right', right: kind === 'sheet' ? 'left' : 'justify' }[estado.align] || 'left';
  const IconoAlineacion = (ALINEACIONES.find(([a]) => a === estado.align) || ALINEACIONES[0])[1];

  let barra;
  if (edicion && teclado) {
    barra = (
      <div className="zo-barra zo-barra-rapida" role="toolbar" aria-label="Formato rápido">
        <Rapido icon={Undo2} label="Deshacer" onClick={() => cmd('undo')} />
        <Rapido icon={Bold} label="Negrita" activo={estado.bold} onClick={() => cmd('bold', !estado.bold)} />
        <Rapido icon={Italic} label="Cursiva" activo={estado.italic} onClick={() => cmd('italic', !estado.italic)} />
        <Rapido icon={Underline} label="Subrayado" activo={estado.underline} onClick={() => cmd('underline', !estado.underline)} />
        {kind !== 'sheet' && <Rapido icon={List} label="Viñetas" onClick={() => cmd('list', 'bullet')} />}
        {kind !== 'sheet' && <Rapido icon={ListOrdered} label="Lista numerada" onClick={() => cmd('list', 'number')} />}
        <Rapido icon={IconoAlineacion} label="Cambiar alineación" onClick={() => cmd('align', siguienteAlineacion)} />
        <Rapido label="Más pequeño" onClick={() => cmd('fontSize', estado.fontSize - 1)}><span className="zo-rapido-a is-chica">A</span></Rapido>
        <span className="zo-rapido-num">{estado.fontSize}</span>
        <Rapido label="Más grande" onClick={() => cmd('fontSize', estado.fontSize + 1)}><span className="zo-rapido-a">A</span></Rapido>
        <Rapido icon={Type} label="Todo el formato" onClick={() => setHoja('formato')} />
        <Rapido icon={Keyboard} label="Ocultar teclado" onClick={ocultarTeclado} />
      </div>
    );
  } else if (edicion) {
    barra = (
      <div className="zo-barra" role="toolbar" aria-label="Edición">
        <BotonBarra icon={Undo2} label="Deshacer" onClick={() => cmd('undo')} disabled={!ready || !estado.canUndo} />
        <BotonBarra icon={Redo2} label="Rehacer" onClick={() => cmd('redo')} disabled={!ready || !estado.canRedo} />
        <BotonBarra icon={Type} label="Formato" onClick={() => setHoja('formato')} disabled={!ready} activo={hoja === 'formato'} testid="zo-movil-formato" />
        <BotonBarra icon={SquarePlus} label="Insertar" onClick={() => setHoja('insertar')} disabled={!ready} activo={hoja === 'insertar'} testid="zo-movil-insertar" />
        {aurora && <BotonBarra label="Aurora IA" onClick={onAurora} disabled={!ready}><AuroraLogo size={22} /></BotonBarra>}
        <BotonBarra icon={MoreHorizontal} label="Más" onClick={() => setHoja('mas')} activo={hoja === 'mas'} testid="zo-movil-mas" />
      </div>
    );
  } else {
    barra = (
      <div className="zo-barra" role="toolbar" aria-label="Lectura">
        <BotonBarra icon={Search} label="Buscar" onClick={() => onAccion('search')} disabled={!ready} />
        {comentar && <BotonBarra icon={MessageSquareText} label="Comentarios" onClick={() => onAccion('comments')} disabled={!ready} />}
        {kind === 'slides'
          ? <BotonBarra icon={Play} label="Presentar" onClick={onPresentar} disabled={!ready} />
          : <BotonBarra icon={Maximize2} label="Ajustar" onClick={() => cmd('zoomFit')} disabled={!ready} />}
        {aurora && <BotonBarra label="Aurora IA" onClick={onAurora} disabled={!ready}><AuroraLogo size={22} /></BotonBarra>}
        {/* Quien entra por el enlace no tiene Aurora: su hueco es para descargar, lo más pedido desde fuera */}
        {guest && descargas.length > 0 && (
          <BotonBarra icon={Download} label="Descargar" onClick={() => setHoja('descargar')} activo={hoja === 'descargar'} testid="zo-movil-descargar" />
        )}
        <BotonBarra icon={MoreHorizontal} label="Más" onClick={() => setHoja('mas')} activo={hoja === 'mas'} testid="zo-movil-mas" />
      </div>
    );
  }

  return (
    <>
      {barra}
      {hoja === 'formato' && <HojaFormato kind={kind} estado={estado} cmd={cmd} onClose={cerrar} />}
      {hoja === 'insertar' && <HojaInsertar kind={kind} cmd={cmd} onClose={cerrar} />}
      {hoja === 'descargar' && (
        <Hoja titulo="Descargar" onClose={cerrar}>
          <ListaDescargas descargas={descargas} titulo={null} onDescargar={(f) => { cerrar(); onDescargar(f); }} />
        </Hoja>
      )}
      {hoja === 'mas' && (
        <Hoja titulo="Más opciones" onClose={cerrar}>
          <Grupo titulo="Ver">
            {edicion && <Cuadro icon={Search} label="Buscar" onClick={() => accion('search')} />}
            {edicion && comentar && <Cuadro icon={MessageSquareText} label="Comentarios" onClick={() => accion('comments')} />}
            {kind === 'doc' && <Cuadro icon={ListTree} label="Índice" onClick={() => accion('navigation')} />}
            {kind === 'slides' && edicion && <Cuadro icon={Play} label="Presentar" onClick={() => { cerrar(); onPresentar(); }} />}
            <Cuadro icon={ZoomOut} label="Alejar" onClick={() => cmd('zoomOut')} />
            <Cuadro icon={ZoomIn} label="Acercar" onClick={() => cmd('zoomIn')} />
            <Cuadro icon={Maximize2} label="Ajustar" onClick={() => { cmd('zoomFit'); cerrar(); }} />
            <Cuadro icon={dark ? Sun : Moon} label={dark ? 'Tema claro' : 'Tema oscuro'} onClick={onTema} />
          </Grupo>
          {!guest && (
            <Grupo titulo="Archivo">
              {aurora && <Cuadro label="Conversar con Aurora" onClick={() => { cerrar(); onAuroraChat(); }}><AuroraLogo size={20} /></Cuadro>}
              {edicion && <Cuadro icon={History} label="Versiones" onClick={() => accion('history')} />}
              {onDestacar && <Cuadro icon={Star} label={starred ? 'Quitar destacado' : 'Destacar'} activo={starred} onClick={onDestacar} />}
              <Cuadro icon={FolderOpen} label="Ver en Drive" onClick={onDrive} />
            </Grupo>
          )}
          {descargas.length > 0 && <ListaDescargas descargas={descargas} onDescargar={(f) => { cerrar(); onDescargar(f); }} />}
          {onBarraCompleta && (
            <Grupo titulo="Avanzado">
              <Cuadro icon={PanelTop} label={barraCompleta ? 'Ocultar barra del editor' : 'Barra completa del editor'} activo={barraCompleta} onClick={() => { cerrar(); onBarraCompleta(); }} />
            </Grupo>
          )}
        </Hoja>
      )}
    </>
  );
}
